import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { X } from 'lucide-react';
import { Button, Text, Input, IconButton } from '../ui';
import useMediaQuery from '../../hooks/useMediaQuery';

const EditOfferingModal = ({ isOpen, onClose, offering, onSave, mode = 'edit' }) => {
  const { isMobile } = useMediaQuery();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      page_url: '',
    },
  });

  useEffect(() => {
    if (offering) {
      reset({
        name: offering.name || '',
        description: offering.description || '',
        page_url: offering.page_url || '',
      });
    } else {
      reset({
        name: '',
        description: '',
        page_url: '',
      });
    }
  }, [offering, reset]);

  const onSubmit = (data) => {
    if (mode === 'add') {
      onSave(data);
    } else if (mode === 'delete') {
      onSave(offering.id);
    } else {
      onSave(offering.id, data);
    }
    onClose();
  };

  const handleConfirmDelete = () => {
    onSave(offering.id);
    onClose();
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className='modal-overlay'>
      <div className='modal-content w-full max-w-2xl overflow-hidden'>
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b' style={{ borderColor: 'var(--border)' }}>
          <Text variant='title' className={`${isMobile ? 'text-base' : ''}`}>
            {mode === 'add'
              ? 'Add Offering'
              : mode === 'delete'
              ? 'Delete Offering'
              : 'Edit Offering'}
          </Text>
          <IconButton icon={X} onClick={handleCancel} variant='ghost' tooltip='Close' />
        </div>

        {/* Delete Confirmation */}
        {mode === 'delete' ? (
          <div className='p-6'>
            <div className='mb-6'>
              <p className={`mb-4 ${isMobile ? 'text-sm' : ''}`} style={{ color: 'var(--text-body)' }}>
                Are you sure you want to delete the offering{' '}
                <span className='font-semibold' style={{ color: 'var(--text-primary)' }}>"{offering?.name}"</span>?
              </p>
              <p className={`${isMobile ? 'text-xs' : 'text-sm'}`} style={{ color: '#ef4444' }}>
                This action cannot be undone. All information related to this offering will be
                permanently removed.
              </p>
            </div>
            <div className='flex items-center justify-end gap-3'>
              <Button type='button' size='md' variant='secondary' onClick={handleCancel}>
                Cancel
              </Button>
              <Button type='button' size='md' variant='danger' onClick={handleConfirmDelete}>
                Delete Offering
              </Button>
            </div>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit(onSubmit)} className='p-6 space-y-6'>
            {/* Title Input */}
            <Controller
              name='name'
              control={control}
              rules={{ required: 'Title is required' }}
              render={({ field }) => (
                <Input
                  {...field}
                  type='text'
                  label='Title'
                  placeholder='Enter offering title'
                  variant='ghost'
                  error={errors.name?.message}
                />
              )}
            />

            {/* Description Input */}
            <Controller
              name='description'
              control={control}
              rules={{ required: 'Description is required' }}
              render={({ field }) => (
                <Input
                  {...field}
                  type='textarea'
                  rows={4}
                  label='Description'
                  placeholder='Describe your offering'
                  variant='ghost'
                  error={errors.description?.message}
                />
              )}
            />

            {/* URL Input */}
            <Controller
              name='page_url'
              control={control}
              rules={{
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: 'Please enter a valid URL',
                },
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  type='url'
                  label='URL'
                  placeholder='https://example.com/offering'
                  variant='ghost'
                  error={errors.page_url?.message}
                />
              )}
            />

            {/* Actions */}
            <div className='flex items-center justify-end gap-3 pt-4'>
              <Button type='button' variant='secondary' size='md' onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type='submit'
                variant='primary'
                size='md'
                disabled={mode === 'edit' && !isDirty}
              >
                {mode === 'add' ? 'Add Offering' : 'Update Offering'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditOfferingModal;
